import { useApolloClient, useQuery } from '@apollo/react-hooks';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Tooltip,
} from '@material-ui/core';
import {
  DataGrid,
  GridColDef,
  GridColumns,
  GridSortModel,
} from '@mui/x-data-grid';
import { gql } from 'apollo-boost';
import Link from 'components/Link';
import ResultModal from 'components/ResultModal';
import useCurrentUser from 'hooks/useCurrentUser';
import { capitalize } from 'lodash';
import MetricTypesField from 'pages/WorkoutLive/MetricTypesField';
import Papa from 'papaparse';
import { ApolloClient } from 'apollo-client';
import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import {
  ActivityIndicator,
  Button,
  IconButton,
  List,
  Menu,
  Text,
  Theme,
} from 'react-native-paper';
import { METRICS, MetricType } from 'shared/components/metrics';
import { WORKOUT_RESULT_FRAGMENT } from 'shared/graphql/fragments';
import { colors, theme } from 'shared/styles';

import TransferResultModal from 'components/TransferResultModal';
import usePreferences from 'hooks/usePreferences';
import { ResultMenu, ResultMenuVisible } from 'pages/UserShow/UserShow';
import {
  DeviceStatus,
  ErgType,
  Interval,
  TrackFeature,
  TrackRole,
  User,
  UserSettings,
  Workout,
  WorkoutResult,
} from 'shared/types';
import {
  calculateDeviceTypes,
  calculatePace,
  formatFitScore,
  formatScoreDisplay,
  getErgBenchmarks,
  getInitialErgBenchmarks,
} from 'shared/utils';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { pm5Firmware } from 'utils/helpers';
import styled from 'utils/styled';

const boldTheme = {
  ...theme,
  fonts: {
    ...theme.fonts,
    regular: theme.fonts.medium,
  },
};

const INITIAL_ERG_BENCHMARKS = getInitialErgBenchmarks();
const fontSize = 14;
const RenderCell = ({
  params,
  metricType,
}: {
  params: any;
  metricType: MetricType;
}) => {
  const RenderInterval = METRICS[metricType].renderCell;
  const RenderOverall = METRICS[metricType].renderHeaderCell;
  const user = params.getValue(params.id, 'user');
  const ergType = params.getValue(params.id, 'ergType');
  const intervals = params.getValue(params.id, 'intervals') as Interval[];
  const ergBenchmarks = params.getValue(params.id, 'ergBenchmarks');

  const overall = (fontTheme: Theme) => (
    <RenderOverall
      theme={fontTheme}
      fontSize={fontSize}
      intervals={intervals}
      ergType={ergType}
      deviceTypes={calculateDeviceTypes(intervals)}
      ergBenchmarks={ergBenchmarks}
      currentUser={user}
      elapsedTime={(params.getValue(params.id, 'time') || 0) * 100}
      elapsedDistance={(params.getValue(params.id, 'distance') || 0) * 10}
      activeIndex={0}
      deviceStatus={DeviceStatus.WorkoutEnded}
      isPro={true}
    />
  );

  return params.getValue(params.id, 'expand') ? (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        lineHeight: 'normal',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'flex-end',
      }}
    >
      {overall(boldTheme)}
      {intervals.map((interval: Interval, index: number) => (
        <RenderInterval
          key={index}
          interval={interval}
          theme={theme}
          fontSize={fontSize}
          currentUser={user}
          ergType={interval.ergType || ergType}
          ergBenchmarks={ergBenchmarks}
          isPro={true}
        />
      ))}
    </div>
  ) : (
    overall(theme)
  );
};

enum ColumnType {
  name = 'name',
  date = 'date',
  score = 'score',
  rx = 'rx',
  status = 'status',
  time = 'time',
  distance = 'distance',
  pace = 'pace',
  c2Id = 'c2Id',
  device = 'device',
  firmware = 'firmware',
  actions = 'actions',
}

enum IntervalMode {
  expand = 'expand',
  pace = 'pace',
  time = 'time',
  distance = 'distance',
}

/** Superadmin-only result columns (non–FIT erg workouts). */
const SUPERADMIN_RESULT_COLUMNS: { field: ColumnType; label: string }[] = [
  { field: ColumnType.c2Id, label: 'C2 Id' },
  { field: ColumnType.device, label: 'Device' },
  { field: ColumnType.firmware, label: 'Firmware' },
];

const FIXED_METRICS = {
  time: MetricType.time,
  distance: MetricType.distance,
  avgPace: MetricType.avgPace,
};

export const availableExtraMetrics = Object.values(MetricType).filter(
  (metric) => !Object.values(FIXED_METRICS).includes(metric)
);

const defaultOptions = {
  width: 160,
  flex: 0.8,
  headerAlign: 'right' as const,
  align: 'right' as const,
};
const RESULT_COLUMNS = [
  {
    field: ColumnType.name,
    headerName: 'Name',
    width: 150,
    flex: 1,
    valueGetter: (params: any) => params.getValue(params.id, 'user').name,
    renderCell: (params: any) => {
      const archivedAt = params.getValue(params.id, 'archivedAt');
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <Link to={`/users/${params.getValue(params.id, 'user').id}/results`}>
            {params.value}
          </Link>
          {!!archivedAt && (
            <Tooltip
              title={`Archived ${new Date(archivedAt).toLocaleDateString()}`}
            >
              <span>
                <IconButton
                  size={16}
                  color={colors.textColor}
                  icon="archive"
                  style={{ marginLeft: 4, opacity: 0.7 }}
                />
              </span>
            </Tooltip>
          )}
        </span>
      );
    },
  },
  {
    field: ColumnType.date,
    headerName: 'Date',
    type: 'date',
    ...defaultOptions,
    valueGetter: (params: any) => params.getValue(params.id, 'insertedAt'),
    renderCell: (params: any) => {
      const notes = params.getValue(params.id, 'notes');
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {!!notes && (
            <Tooltip title={notes}>
              <span>
                <IconButton
                  size={16}
                  color={colors.textColor}
                  icon="note-outline"
                ></IconButton>
              </span>
            </Tooltip>
          )}
          {new Date(params.value).toLocaleDateString()}
        </span>
      );
    },
  },
  {
    field: ColumnType.score,
    headerName: 'Score',
    ...defaultOptions,
    valueGetter: (params: any) => params.getValue(params.id, 'scoreValue'),
    renderCell: (params: any) =>
      formatFitScore(params.value, params.getValue(params.id, 'scoreType')),
  },
  {
    field: ColumnType.rx,
    headerName: 'Rx',
    type: 'boolean',
    ...defaultOptions,
    valueGetter: (params: any) => params.getValue(params.id, 'isRx'),
  },
  {
    field: ColumnType.status,
    headerName: 'Status',
    ...defaultOptions,
    valueGetter: (params: any) =>
      capitalize(
        params.value ||
          (params.getValue(params.id, 'hasScore') ? 'ranked' : 'not ranked')
      ),
  },
  {
    field: ColumnType.time,
    headerName: 'Time',
    ...defaultOptions,
    renderCell: (params: any) => (
      <RenderCell params={params} metricType={FIXED_METRICS.time} />
    ),
  },
  {
    field: ColumnType.distance,
    headerName: 'Distance',
    ...defaultOptions,
    renderCell: (params: any) => (
      <RenderCell params={params} metricType={FIXED_METRICS.distance} />
    ),
  },
  {
    field: ColumnType.pace,
    headerName: 'Pace',
    ...defaultOptions,
    renderCell: (params: any) => (
      <RenderCell params={params} metricType={FIXED_METRICS.avgPace} />
    ),
  },
  {
    field: ColumnType.c2Id,
    headerName: 'C2 Id',
    ...defaultOptions,
    valueGetter: (params: any) => params.getValue(params.id, 'concept2Id'),
  },
  {
    field: ColumnType.device,
    headerName: 'Device',
    ...defaultOptions,
    valueGetter: (params: any) =>
      params.getValue(params.id, 'metadata').deviceOs,
  },
  {
    field: ColumnType.firmware,
    headerName: 'Firmware',
    ...defaultOptions,
    valueGetter: (params: any) =>
      pm5Firmware(params.getValue(params.id, 'metadata')),
  },
  {
    field: ColumnType.actions,
    headerName: 'Actions',
    width: 100,
    flex: 0.4,
    headerAlign: 'right',
    align: 'right',
    sortable: false,
    renderCell: (params: any) => (
      <IconButton
        icon="dots-vertical"
        // @ts-ignore
        onPress={(e) => {
          // @ts-ignore
          const rect = e.target.getBoundingClientRect();
          params.getValue(
            params.id,
            'setResultMenuVisible'
          )({
            resultId: params.id,
            archivedAt: params.getValue(params.id, 'archivedAt'),
            hasForceCurveCsv: params.getValue(params.id, 'hasForceCurveCsv'),
            anchor: { x: rect.left, y: rect.top },
          });
        }}
      />
    ),
  },
  ...availableExtraMetrics.map((metric) => {
    return {
      field: `Metric-${metric}`,
      headerName: METRICS[metric].header({
        ergType: ErgType.row,
        ergBenchmarks: INITIAL_ERG_BENCHMARKS,
      }),
      ...defaultOptions,
      sortable: false,
      renderCell: (params: any) => (
        <RenderCell params={params} metricType={metric} />
      ),
    };
  }),
] as GridColumns;

const GET_WORKOUT = gql`
  query GetWorkoutResults(
    $id: ID!
    $trackId: ID!
    $offset: Int
    $limit: Int
    $userDisplay: Int
  ) {
    workout(id: $id) {
      id
      allResults(offset: $offset, limit: $limit, showAll: true) {
        ...ResultFragment
        hasForceCurveCsv
        archivedAt
        concept2Id
        metadata {
          deviceOs
          pm5Fw
        }
        user {
          id
          name(display: $userDisplay)
          compRegistration(trackId: $trackId) {
            gender
            divisionDisplay
          }
          settings {
            maxHeartRate
            restingHeartRate
            weight
            weightUnit
            age
            benchmarkValues {
              distance
              name
              scoreNumber
              group
              field
              ergType
            }
          }
        }
      }
    }
  }
  ${WORKOUT_RESULT_FRAGMENT}
`;

interface UserData extends User {
  settings: UserSettings;
  compRegistration?: {
    gender?: string | null;
    divisionDisplay?: string | null;
  };
}

interface WorkoutResultData extends WorkoutResult {
  user: UserData;
}

interface WorkoutData extends Workout {
  allResults: WorkoutResultData[];
}

interface Query {
  workout: WorkoutData;
}

const CSV_FETCH_PAGE_SIZE = 1000;

async function fetchAllWorkoutResultsForCsv(
  client: ApolloClient<any>,
  args: {
    id: string;
    trackId: string;
    userDisplay: number;
    totalCount: number;
  }
): Promise<WorkoutResultData[]> {
  const { totalCount, id, trackId, userDisplay } = args;
  if (totalCount <= 0) return [];

  const all: WorkoutResultData[] = [];
  const pageCount = Math.ceil(totalCount / CSV_FETCH_PAGE_SIZE);

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
    const offset = pageIndex * CSV_FETCH_PAGE_SIZE;
    const res = await client.query<Query>({
      query: GET_WORKOUT,
      variables: {
        id,
        trackId,
        userDisplay,
        offset,
        limit: CSV_FETCH_PAGE_SIZE,
      },
      fetchPolicy: 'network-only',
    });
    const batch = res.data?.workout?.allResults ?? [];
    all.push(...batch);
  }

  return all;
}

/** Wait until DataGrid has painted the expected number of rows (export uses DOM scraping). */
function waitForDomGridRows(
  gridContainerId: string,
  expectedRowCount: number,
  options?: { timeoutMs?: number }
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? 120000;
  const deadline = Date.now() + timeoutMs;

  return new Promise((resolve, reject) => {
    const check = () => {
      const node = document.getElementById(gridContainerId);
      const rendered = node?.querySelectorAll('.MuiDataGrid-row').length ?? 0;

      if (rendered === expectedRowCount) {
        requestAnimationFrame(() => resolve());
        return;
      }

      if (Date.now() > deadline) {
        reject(
          new Error(
            `CSV export: grid has ${rendered} rows, expected ${expectedRowCount}`
          )
        );
        return;
      }

      requestAnimationFrame(check);
    };

    requestAnimationFrame(check);
  });
}

const useStyles = makeStyles({
  root: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '&.MuiDataGrid-root .MuiDataGrid-cell': {
      cursor: 'pointer',
    },
    borderWidth: 0,
  },
});

const ActionsContainer = styled.view({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  margin: 10,
});

const limitOptions = [10, 25, 50, 100, 1000];

function formatCompGender(gender?: string | null) {
  return gender || '—';
}

/** 0 -> A, 25 -> Z, 26 -> AA (for many score columns). */
function scoreColumnLetters(index: number): string {
  let s = '';
  let n = index;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

function compScoreColumnTitle(index: number) {
  return `Score ${scoreColumnLetters(index)}`;
}

interface Props {
  resultsCount: number;
  workout: Workout;
  ergType: ErgType;
}

export default function ResultsTable({
  workout,
  resultsCount,
  ergType,
}: Props) {
  const { track } = workout;
  const apolloClient = useApolloClient();
  const { currentUser } = useCurrentUser();
  const classes = useStyles();
  const [resultId, setResultId] = useQueryParam('id', StringParam);
  const [resultMenuVisible, setResultMenuVisible] = useState<
    ResultMenuVisible | undefined
  >();
  const [showTransferModal, setShowTransferModal] = useState<{
    resultId: string;
  } | null>(null);
  const { resultMetricTypes, setResultMetricTypes } = usePreferences();
  const [intervalsMode, setIntervalsMode] = useQueryParam(
    'intervals',
    StringParam
  );
  const [showMetricsSettings, setShowMetricsSettings] = useState(false);
  const [showCompMetricsSettings, setShowCompMetricsSettings] = useState(false);
  const [compShowDivision, setCompShowDivision] = useState(false);
  const [compShowGender, setCompShowGender] = useState(false);
  const [compShowScores, setCompShowScores] = useState(false);
  const [showSuperadminColumnsSettings, setShowSuperadminColumnsSettings] =
    useState(false);
  const [superadminColumnsVisible, setSuperadminColumnsVisible] = useState(() =>
    SUPERADMIN_RESULT_COLUMNS.reduce<Record<string, boolean>>((acc, c) => {
      acc[c.field] = false;
      return acc;
    }, {})
  );
  const [pageParam, setPage] = useQueryParam('page', NumberParam);
  const page = pageParam || 1;
  const [limitParam, setLimit] = useQueryParam('limit', NumberParam);
  const limit = limitOptions.includes(limitParam || 0)
    ? limitParam!
    : limitOptions[2];
  const { data, loading, refetch } = useQuery<Query>(GET_WORKOUT, {
    fetchPolicy: 'cache-and-network',
    variables: {
      id: workout.id,
      trackId: track.id,
      offset: (page - 1) * limit,
      limit,
      userDisplay: track.userDisplay,
    },
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: ColumnType.date, sort: 'desc' },
  ]);
  const [csvExportBusy, setCsvExportBusy] = useState(false);
  const [csvExportSnapshot, setCsvExportSnapshot] = useState<
    WorkoutResultData[] | null
  >(null);

  const expand = intervalsMode === IntervalMode.expand;

  useEffect(() => {
    if (csvExportSnapshot === null) return;
    let cancelled = false;

    const run = async () => {
      try {
        await waitForDomGridRows('results-grid', csvExportSnapshot.length);
        if (cancelled) return;
        downloadCsv(workout.title, expand);
        setCsvExportSnapshot(null);
        setCsvExportBusy(false);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCsvExportSnapshot(null);
          setCsvExportBusy(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [csvExportSnapshot, workout.title, expand]);

  const results =
    csvExportSnapshot ?? (data && data.workout ? data.workout.allResults : []);
  const scoreColumnCount = workout.scoreOptions?.length ?? 0;
  const rows = useMemo(() => {
    if (!workout) return [];

    return results.map((result) => {
      const intervals = result.intervals.map((i) => ({
        ...i,
        time: i.elapsedTime,
        distance: i.elapsedDistance,
        pace: i.avgPace,
      }));
      const ergBenchmarks = getErgBenchmarks(
        intervals,
        true,
        result.user ? result.user.settings : undefined,
        'A'
      );
      return {
        ...result,
        workout,
        scoreType: workout.scoreType,
        time: (result.elapsedTime || 0) / 100,
        distance: (result.elapsedDistance || 0) / 10,
        pace: calculatePace(
          (result.elapsedTime || 0) / 100,
          (result.elapsedDistance || 0) / 10,
          result.ergType
        ),
        setResultMenuVisible,
        intervals,
        ergBenchmarks,
        expand,
      };
    });
  }, [results, workout, expand, setResultMenuVisible]);

  const intervalsHeight =
    expand && results.length ? results[0].intervals.length * 18 : 0;

  const columns = useMemo(() => {
    const types = [ColumnType.name, ColumnType.date];

    if (workout) {
      if (track.compStatus) {
        types.push(ColumnType.status);
      }
      if (workout.ergType === ErgType.fit) {
        types.push(ColumnType.score);
        types.push(ColumnType.rx);
      } else {
        if (currentUser.superadmin) {
          SUPERADMIN_RESULT_COLUMNS.forEach(({ field }) => {
            if (superadminColumnsVisible[field]) types.push(field);
          });
        }
        types.push(ColumnType.time);
        types.push(ColumnType.distance);
        types.push(ColumnType.pace);

        resultMetricTypes.forEach((metric) => {
          types.push(`Metric-${metric}` as any);
        });
      }
    }
    types.push(ColumnType.actions);
    const fields = RESULT_COLUMNS.reduce<{ [key: string]: number }>(
      (acc, c, i) => ({
        ...acc,
        [c.field]: i,
      }),
      {}
    );

    let cols = types
      .map((t) => RESULT_COLUMNS[fields[t]])
      .reduce<GridColDef[]>((acc, col) => {
        acc.push(col);
        if (col.field === ColumnType.pace) {
          if (workout && intervalsMode === IntervalMode.pace) {
            const RenderInterval = METRICS[FIXED_METRICS.avgPace].renderCell;
            workout.intervals.forEach((_, index) => {
              acc.push({
                field: `Interval-${index}`,
                headerName: `# ${index + 1}`,
                sortable: false,
                ...defaultOptions,
                renderCell: (params: any) => {
                  const user = params.getValue(params.id, 'user');
                  const ergType = params.getValue(params.id, 'ergType');
                  const ergBenchmarks = params.getValue(
                    params.id,
                    'ergBenchmarks'
                  );
                  const intervals = params.getValue(
                    params.id,
                    'intervals'
                  ) as Interval[];
                  const interval = intervals[index];

                  if (interval) {
                    return (
                      <RenderInterval
                        interval={interval}
                        theme={theme}
                        fontSize={fontSize}
                        currentUser={user}
                        ergType={interval.ergType || ergType}
                        ergBenchmarks={ergBenchmarks}
                        isPro={true}
                      />
                    );
                  } else {
                    return <Text>-</Text>;
                  }
                },
              } as GridColDef);
            });
          }
        }

        if (col.field === ColumnType.time) {
          if (workout && intervalsMode === IntervalMode.time) {
            const RenderInterval = METRICS[FIXED_METRICS.time].renderCell;
            workout.intervals.forEach((_, index) => {
              acc.push({
                field: `Interval-${index}`,
                headerName: `# ${index + 1}`,
                sortable: false,
                ...defaultOptions,
                renderCell: (params: any) => {
                  const user = params.getValue(params.id, 'user');
                  const ergType = params.getValue(params.id, 'ergType');
                  const ergBenchmarks = params.getValue(
                    params.id,
                    'ergBenchmarks'
                  );
                  const intervals = params.getValue(
                    params.id,
                    'intervals'
                  ) as Interval[];
                  const interval = intervals[index];

                  if (interval) {
                    return (
                      <RenderInterval
                        interval={interval}
                        theme={theme}
                        fontSize={fontSize}
                        currentUser={user}
                        ergType={interval.ergType || ergType}
                        ergBenchmarks={ergBenchmarks}
                        isPro={true}
                      />
                    );
                  } else {
                    return <Text>-</Text>;
                  }
                },
              } as GridColDef);
            });
          }
        }

        if (col.field === ColumnType.distance) {
          if (workout && intervalsMode === IntervalMode.distance) {
            const RenderInterval = METRICS[FIXED_METRICS.distance].renderCell;
            workout.intervals.forEach((_, index) => {
              acc.push({
                field: `Interval-${index}`,
                headerName: `# ${index + 1}`,
                sortable: false,
                ...defaultOptions,
                renderCell: (params: any) => {
                  const user = params.getValue(params.id, 'user');
                  const ergType = params.getValue(params.id, 'ergType');
                  const ergBenchmarks = params.getValue(
                    params.id,
                    'ergBenchmarks'
                  );
                  const intervals = params.getValue(
                    params.id,
                    'intervals'
                  ) as Interval[];
                  const interval = intervals[index];

                  if (interval) {
                    return (
                      <RenderInterval
                        interval={interval}
                        theme={theme}
                        fontSize={fontSize}
                        currentUser={user}
                        ergType={interval.ergType || ergType}
                        ergBenchmarks={ergBenchmarks}
                        isPro={true}
                      />
                    );
                  } else {
                    return <Text>-</Text>;
                  }
                },
              } as GridColDef);
            });
          }
        }

        return acc;
      }, []);

    if (track.compStatus) {
      const compCols: GridColDef[] = [];
      if (compShowDivision) {
        compCols.push({
          field: 'compDivision',
          headerName: 'Division',
          width: 140,
          flex: 0.6,
          sortable: false,
          valueGetter: (params: any) => {
            const u = params.getValue(params.id, 'user') as UserData;
            return u?.compRegistration?.divisionDisplay ?? '';
          },
          renderCell: (params: any) => params.value || '—',
        });
      }
      if (compShowGender) {
        compCols.push({
          field: 'compGender',
          headerName: 'Gender',
          width: 100,
          flex: 0.4,
          sortable: false,
          valueGetter: (params: any) => {
            const u = params.getValue(params.id, 'user') as UserData;
            return formatCompGender(u?.compRegistration?.gender);
          },
        });
      }
      if (compShowScores && scoreColumnCount > 0) {
        for (let i = 0; i < scoreColumnCount; i++) {
          compCols.push({
            field: `compScore-${i}`,
            headerName: compScoreColumnTitle(i),
            ...defaultOptions,
            sortable: false,
            renderCell: (params: any) => {
              const scores = params.getValue(params.id, 'compScores') as
                | { scoreOption: number; scoreValue?: number }[]
                | undefined;
              const rowScore = scores?.find((s) => s.scoreOption === i);
              return formatScoreDisplay(
                {
                  scoreValue: rowScore?.scoreValue,
                  scoreOption: i,
                },
                workout
              );
            },
          });
        }
      }
      if (compCols.length) {
        const dateIdx = cols.findIndex((c) => c.field === ColumnType.date);
        const insertAt = dateIdx >= 0 ? dateIdx + 1 : 0;
        cols = [
          ...cols.slice(0, insertAt),
          ...compCols,
          ...cols.slice(insertAt),
        ];
      }
    }

    return cols;
  }, [
    workout,
    track,
    resultMetricTypes,
    currentUser,
    intervalsMode,
    compShowDivision,
    compShowGender,
    compShowScores,
    scoreColumnCount,
    superadminColumnsVisible,
  ]);

  if (!workout) return <ActivityIndicator style={{ margin: 50 }} />;

  const showActions = ergType !== ErgType.fit;

  return (
    <>
      <ActionsContainer>
        {!!track.compStatus && (
          <Menu
            visible={showCompMetricsSettings}
            onDismiss={() => setShowCompMetricsSettings(false)}
            anchor={
              <Button
                icon="trophy-outline"
                mode="outlined"
                style={{ marginRight: 8 }}
                onPress={() => setShowCompMetricsSettings(true)}
              >
                Comp info
              </Button>
            }
          >
            <View style={{ padding: 16, minWidth: 220 }}>
              <Text style={{ marginBottom: 8, fontWeight: '600' }}>
                Visible columns
              </Text>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={compShowDivision}
                      onChange={(_, checked) => setCompShowDivision(checked)}
                    />
                  }
                  label="Division"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={compShowGender}
                      onChange={(_, checked) => setCompShowGender(checked)}
                    />
                  }
                  label="Gender"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={compShowScores}
                      onChange={(_, checked) => setCompShowScores(checked)}
                      disabled={scoreColumnCount === 0}
                    />
                  }
                  label={
                    scoreColumnCount === 0
                      ? 'Scores (none configured)'
                      : `Scores (${scoreColumnCount})`
                  }
                />
              </FormGroup>
            </View>
          </Menu>
        )}

        {currentUser.superadmin && workout.ergType !== ErgType.fit && (
          <Menu
            visible={showSuperadminColumnsSettings}
            onDismiss={() => setShowSuperadminColumnsSettings(false)}
            anchor={
              <Button
                icon="shield-account-outline"
                mode="outlined"
                style={{ marginRight: 8 }}
                onPress={() => setShowSuperadminColumnsSettings(true)}
              >
                Admin columns
              </Button>
            }
          >
            <View style={{ padding: 16, minWidth: 220 }}>
              <Text style={{ marginBottom: 8, fontWeight: '600' }}>
                Superadmin columns
              </Text>
              <FormGroup>
                {SUPERADMIN_RESULT_COLUMNS.map(({ field, label }) => (
                  <FormControlLabel
                    key={field}
                    control={
                      <Checkbox
                        color="primary"
                        checked={!!superadminColumnsVisible[field]}
                        onChange={(_, checked) =>
                          setSuperadminColumnsVisible((prev) => ({
                            ...prev,
                            [field]: checked,
                          }))
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            </View>
          </Menu>
        )}

        {showActions && (
          <>
            <Menu
              visible={showMetricsSettings}
              onDismiss={() => setShowMetricsSettings(false)}
              anchor={
                <Button
                  icon="table-edit"
                  mode="outlined"
                  onPress={() => {
                    setShowMetricsSettings(true);
                  }}
                >
                  Change Metrics
                </Button>
              }
            >
              <View style={{ width: '90vw', maxWidth: 300 }}>
                <List.Item title="Metric 1" right={() => <Text>Time</Text>} />
                <List.Item
                  title="Metric 2"
                  right={() => <Text>Distance</Text>}
                />
                <List.Item title="Metric 3" right={() => <Text>Pace</Text>} />
                <ScrollView>
                  <MetricTypesField
                    availableMetrics={availableExtraMetrics}
                    metricTypes={resultMetricTypes}
                    setMetricTypes={setResultMetricTypes}
                    startIndexLabel={4}
                  />
                </ScrollView>
              </View>
            </Menu>
            <FormControl
              variant="outlined"
              style={{
                marginRight: 10,
                marginLeft: 8,
                marginTop: 4,
                minWidth: 160,
              }}
              margin="dense"
            >
              <InputLabel htmlFor="intervals-select">
                Interval Column
              </InputLabel>

              <Select
                value={intervalsMode}
                onChange={(event: any) => setIntervalsMode(event.target.value)}
                id="intervals-select"
                label="Intervals"
              >
                <MenuItem value="">
                  <em>Clear</em>
                </MenuItem>
                {[
                  IntervalMode.expand,
                  IntervalMode.pace,
                  IntervalMode.time,
                  IntervalMode.distance,
                ].map((mode) => {
                  return (
                    <MenuItem key={mode} value={mode}>
                      {intervalModeDisplay(mode)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </>
        )}

        <Button
          style={{ marginLeft: 'auto' }}
          icon="file-download-outline"
          mode="text"
          loading={csvExportBusy}
          disabled={csvExportBusy}
          onPress={async () => {
            setCsvExportBusy(true);
            try {
              const allResults = await fetchAllWorkoutResultsForCsv(
                apolloClient,
                {
                  id: workout.id,
                  trackId: track.id,
                  userDisplay: track.userDisplay,
                  totalCount: resultsCount,
                }
              );
              setCsvExportSnapshot(allResults);
            } catch (err) {
              console.error(err);
              setCsvExportBusy(false);
            }
          }}
        >
          Download CSV
        </Button>
      </ActionsContainer>

      <div style={{ display: 'flex', height: '100%' }}>
        <div id="results-grid" style={{ flexGrow: 1 }}>
          <DataGrid
            className={classes.root}
            autoHeight
            rows={rows}
            rowHeight={52 + intervalsHeight}
            onCellClick={(params) => {
              if (
                params.field !== ColumnType.name &&
                params.field !== ColumnType.actions
              ) {
                setResultId(params.id as string, 'replaceIn');
              }
            }}
            // @ts-ignore
            columns={columns}
            disableSelectionOnClick
            loading={csvExportSnapshot ? false : loading || csvExportBusy}
            sortModel={sortModel}
            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
            sortingOrder={['asc', 'desc']}
            disableColumnMenu
            components={{
              ColumnResizeIcon: () => null,
            }}
            {...(csvExportSnapshot
              ? {
                  pagination: true,
                  paginationMode: 'client' as const,
                  page: 0,
                  pageSize: Math.max(csvExportSnapshot.length, 1),
                  hideFooter: true,
                  disableVirtualization: true,
                  rowCount: csvExportSnapshot.length,
                  onPageChange: undefined,
                  onPageSizeChange: undefined,
                }
              : {
                  pagination: true,
                  paginationMode: 'server' as const,
                  hideFooter: false,
                  disableVirtualization: false,
                  page: page - 1,
                  pageSize: limit,
                  onPageSizeChange: (newPageSize: number) =>
                    setLimit(newPageSize),
                  rowsPerPageOptions: limitOptions,
                  rowCount: resultsCount,
                  onPageChange: (newPage: number) => setPage(newPage + 1),
                })}
          />
        </div>
        {resultId && (
          <ResultModal
            resultId={resultId}
            onDismiss={() => setResultId(undefined, 'replaceIn')}
          />
        )}
        {showTransferModal && (
          <TransferResultModal
            visible={!!showTransferModal}
            resultId={showTransferModal.resultId}
            trackId={track.id}
            userDisplay={track.userDisplay}
            onDismiss={() => setShowTransferModal(null)}
            onSelectUser={() => refetch()}
          />
        )}
        <ResultMenu
          resultMenuVisible={resultMenuVisible}
          setResultMenuVisible={setResultMenuVisible}
          onOpenResult={(id) => setResultId(id, 'replaceIn')}
          onOpenTransfer={
            track.role === TrackRole.Admin &&
            track.features.includes(TrackFeature.Impersonate)
              ? (resultId) => setShowTransferModal({ resultId })
              : undefined
          }
          onArchive={
            track.role === TrackRole.Admin &&
            track.features.includes(TrackFeature.Impersonate)
              ? () => refetch()
              : undefined
          }
        />
      </div>
    </>
  );
}

function intervalModeDisplay(mode: IntervalMode): React.ReactNode {
  switch (mode) {
    case IntervalMode.expand:
      return 'Expanded';
    case IntervalMode.pace:
      return 'By Pace';
    case IntervalMode.time:
      return 'By Time';
    case IntervalMode.distance:
      return 'By Distance';

    default:
      return mode;
  }
}

function getSvgTitle(cell: any) {
  const svg = cell.querySelector('svg title');
  return ((svg && svg.textContent) || '').trim();
}

function downloadCsv(workoutName: string, expand: boolean | null | undefined) {
  const node = document.getElementById('results-grid');
  if (!node) return;
  const headers = Array.from(
    node.querySelectorAll('.MuiDataGrid-columnHeader')
  ).map((cell: any) => cell.innerText);
  headers.pop();
  const rows = Array.from(node.querySelectorAll('.MuiDataGrid-row')).reduce<
    string[][]
  >(
    (acc, row: any) => {
      const cells = Array.from(row.querySelectorAll('.MuiDataGrid-cell')).map(
        (cell: any) =>
          cell.innerText
            .replace(/\s+%/g, '%')
            .replace(/[^a-zA-Z0-9 -.:%/\s]/g, '')
            .trim() || getSvgTitle(cell)
      );
      cells.pop();

      const normalizeValue = (v: string) => (v === '-' ? '' : v);

      if (expand) {
        const allValues = cells.map((text) => text.split('\n'));
        const maxLength = Math.max(...allValues.map((el) => el.length));
        Array.from(Array(maxLength).keys()).forEach((i) => {
          acc.push(allValues.map((value) => normalizeValue(value[i])));
        });
      } else {
        acc.push(cells.map(normalizeValue));
      }
      return acc;
    },
    [headers]
  );

  const csv = Papa.unparse(rows);
  const file = new Blob([csv], { type: 'text/csv' });
  const link = document.createElement('a');
  link.download = `${workoutName}.csv`;
  link.href = window.URL.createObjectURL(file);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
